/**
 * ExpandableOrderRow
 *
 * One Order Queue row: a grid with exactly one cell per track in
 * `layout.columns` (queue-columns.ts), using the same template as the column
 * header so every row lines up. Radix Collapsible expands the line items.
 *
 * Exception pills (TEST, hold countdown, Portless, Resolved, Archived SKU,
 * Hybrid) are built once per row and shown in the wide Flags cell, or behind
 * the flag button at compact widths; each person's Display settings decide
 * which of them show.
 */

import { Fragment, memo, useMemo, useState, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as Collapsible from '@radix-ui/react-collapsible';
import {
  ChevronRight,
  CircleDashed,
  Clock,
  CheckCircle2,
  MessagesSquare,
  MoreHorizontal,
  Pause,
  FastForward,
  PackageX,
  Pencil,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { TestOrderBadge } from '@/components/TestOrderBadge';
import { ClientOrgChip } from '@/components/ClientOrgChip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { classifyPortlessFulfilment, stripOrderNamePrefix } from '@threadheads/shared';
import { hybridBadge, labelReadyBadge } from '@/lib/portless-fulfilment-display';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { DuePill } from '@/features/manual-orders/components/DuePill';
import { ArchivedSkuPill, OrderLineItemsTable } from './OrderLineItemsTable';
import { OrderFlagsButton } from './OrderFlagsButton';
import { OosStatusPopover } from './OosStatusPopover';
import {
  DEFAULT_QUEUE_LAYOUT,
  QUEUE_ROW_CHROME_CLASS,
  QUEUE_ROW_PADDING_CLASS,
  QUEUE_ROW_PADDING_PX,
  orderColumnOffsetPx,
  type QueueColumnKey,
  type QueueColumnsLayout,
} from './queue-columns';
import { ProductionStatusBadge } from '../ProductionStatusBadge';
import type { QueuedOrder, DisplayStatus } from '../../types';
import { isStockBlocked, isStrandedInProduction } from '../../utils/order-eligibility';
import { ORDER_QUEUE_BADGE_DEFAULTS, type OrderQueueBadgeVisibility } from '../../display-prefs';

/**
 * ONE pill size for every small pill on the row (Type, Client, TEST, every
 * flag, Resolved): the sm Badge (text-[10px], py-px, rounded-md) with an
 * explicit 16px line height, so they all render at the same height.
 */
const ROW_PILL = 'leading-4';
const FLAG_PILL = cn('shrink-0 whitespace-nowrap', ROW_PILL);
/** TEST at the same size: the sm TestOrderBadge with the sm Badge's 1px padding and radius. */
const TEST_PILL = 'py-px rounded-md';

interface FlagPill {
  key: string;
  /** Plain label, for the flag button's screen-reader description. */
  label: string;
  node: ReactNode;
  isTest?: boolean;
}

/**
 * Format order age as human-readable relative time
 */
function formatAge(orderDate: string): string {
  const now = new Date();
  const date = new Date(orderDate);
  const diffMs = now.getTime() - date.getTime();

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (days > 0) {
    return `${days}d`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return 'now';
}

/**
 * Format remaining hold time as human-readable string (e.g., "1h 45m")
 */
function formatHoldRemaining(heldUntil: string): string | null {
  const remaining = new Date(heldUntil).getTime() - Date.now();
  if (remaining <= 0) return null;
  const minutes = Math.ceil(remaining / 60_000);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${minutes}m`;
}


interface ExpandableOrderRowProps {
  /** The order data to display */
  order: QueuedOrder;
  /** Whether the row is expanded */
  isExpanded: boolean;
  /** Callback to toggle expand state */
  onToggleExpand: (orderId: string) => void;
  /** Shopify domain for admin links */
  shopifyDomain?: string | null;
  /** Whether the order is selected (for batch creation) */
  isSelected?: boolean;
  /** Callback to toggle selection */
  onToggleSelect?: (orderId: string, event?: React.MouseEvent | React.KeyboardEvent) => void;
  /** Whether to show the checkbox */
  showCheckbox?: boolean;
  /**
   * BLU-389: caller's organization id. When `order.organization_id !== callerOrganizationId`,
   * the row is cross-org (a 3PL operator viewing a client's order) and renders the client ribbon.
   */
  callerOrganizationId?: string;
  /**
   * Put-this-order-on-hold action (the trailing ⋮ kebab menu). When provided, the row
   * renders an order-actions menu whose first item is "Put Order On Hold". Omitted →
   * no kebab (e.g. read-only surfaces).
   */
  onPutOnHold?: (order: QueuedOrder) => void;
  /**
   * Skip-the-2-hour-hold action (the trailing ⋮ kebab menu). When provided AND the
   * order is currently held (`isHeld`), the menu renders a "Skip Waiting Period" item
   * that clears this ONE order's `held_until` so it's immediately batchable.
   */
  onSkipHold?: (order: QueuedOrder) => void;
  /**
   * BLU-3PL client alias: owner org id -> display name, keyed by
   * `order.organization_id`. When the caller has set a nickname for a
   * client, the cross-org badge shows it instead of the generic
   * `clientOrderBadge` label. Falls back to the generic label when the
   * order's org isn't in the map.
   */
  orgIdToName?: Record<string, string>;
  /** Caller may open the Client messages inbox (orders.view). Same value for every row. */
  canOpenClientChat?: boolean;
  /**
   * Caller's role may open /production/issues (canOpenIssuesPage). Gates the
   * OOS badge's link: a denied role would be silently redirected away.
   */
  canOpenIssues?: boolean;
  /** Caller's unread order_message count for this row's ERP order (0 when none). */
  clientChatUnread?: number;
  /**
   * The page's column layout (same object for the header and every row). It
   * decides which tracks exist, including the client and chat columns.
   */
  layout?: QueueColumnsLayout;
  /** The caller's Display settings: which exception pills show. */
  badgeVisibility?: OrderQueueBadgeVisibility;
  /**
   * Phase 104 (D-21): caller holds orders.create, so an unbatched manual
   * order's expanded row links to its edit page. Same value for every row.
   */
  canEditManualOrders?: boolean;
}

export const ExpandableOrderRow = memo(function ExpandableOrderRow({
  order,
  isExpanded,
  onToggleExpand,
  shopifyDomain,
  isSelected = false,
  onToggleSelect,
  showCheckbox = false,
  callerOrganizationId,
  onPutOnHold,
  onSkipHold,
  orgIdToName,
  canOpenClientChat = false,
  canOpenIssues = false,
  clientChatUnread = 0,
  layout = DEFAULT_QUEUE_LAYOUT,
  badgeVisibility = ORDER_QUEUE_BADGE_DEFAULTS,
  canEditManualOrders = false,
}: ExpandableOrderRowProps) {
  // BLU-389: cross-org indicator for 3PL operators viewing a client's order at a shared location.
  const isCrossOrg = !!callerOrganizationId && order.organization_id !== callerOrganizationId;
  const { t } = useTranslation('production');
  // Format order number: use order_name if available, otherwise fallback to last 6 digits
  const orderNumber = useMemo(() => {
    if (order.order_number) {
      return stripOrderNamePrefix(order.order_number);
    }
    // Fallback to last 6 digits of shopify_order_id
    return String(order.shopify_order_id).slice(-6);
  }, [order.order_number, order.shopify_order_id]);

  // Calculate total quantity across all line items
  const totalQuantity = useMemo(() =>
    order.line_items.reduce((sum, item) => sum + item.quantity, 0),
    [order.line_items]
  );

  // Any line on a SKU archived in this order's own org (server-derived flag).
  const hasArchivedSku = useMemo(
    () => order.line_items.some((li) => li.sku_archived === true),
    [order.line_items]
  );

  // Hybrid — classified from the order tags by the SHARED rule in
  // @threadheads/shared, the same one that decides which Portless manifest sheet
  // the order's lines land on and whether the packing SKU sticker prints. Never
  // re-test the tags here: two copies of this rule already caused one production
  // inconsistency.
  //
  // `null` for Cross Dock, which gets no badge — see portless-fulfilment-display.ts.
  const hybrid = useMemo(() => hybridBadge(classifyPortlessFulfilment(order.tags)), [order.tags]);

  // Determine fulfillment types from line items
  const { hasPod, hasPap } = useMemo(() => {
    let pod = false;
    let pap = false;
    for (const item of order.line_items) {
      if (item.production_type === 'POD') pod = true;
      if (item.production_type === 'PAP') pap = true;
      if (pod && pap) break; // No need to continue
    }
    return { hasPod: pod, hasPap: pap };
  }, [order.line_items]);

  // Hold indicator: countdown for orders inside the customer-editing window.
  // `holdText` is the reactive source of truth — formatHoldRemaining returns null
  // once the hold expires, and the 30s interval below re-evaluates it. `isHeld`
  // is only used to seed the initial state (and to gate the Skip-Waiting-Period
  // menu item); everything else reads `holdText`.
  const isHeld = !!(order.held_until && new Date(order.held_until) > new Date());
  const [holdText, setHoldText] = useState<string | null>(
    isHeld ? formatHoldRemaining(order.held_until!) : null
  );

  useEffect(() => {
    // BLU-718: MUST clear on the way out, not just bail. `holdText` now gates
    // the ON_HOLD badge, the click handler and the disabled checkbox — so
    // leaving a stale value here made "Skip Waiting Period" (which nulls
    // held_until) look like it did nothing: the row kept a frozen countdown
    // and an unclickable checkbox until the operator navigated away.
    if (!order.held_until) {
      setHoldText(null);
      return;
    }
    const update = () => setHoldText(formatHoldRemaining(order.held_until!));
    update();
    const interval = setInterval(update, 30_000);
    return () => clearInterval(interval);
  }, [order.held_until]);

  // The order's live garment units already cover it, yet its prod_orders row
  // still reads NEW (or WAITING_STOCK). This is the redo-adopted-by-another-
  // batch case: routing the order reset the parent row, and the pickup at the
  // new floor moved only the unit's batch_id. Create Batch refuses such an
  // order, so the row must neither claim "Ready" nor offer a checkbox.
  // Genuinely batched orders keep their real stage badge (Printing, Binned...)
  // because their production_status is no longer NEW.
  const isAlreadyInProduction = useMemo(
    () => isStrandedInProduction(order),
    [order]
  );

  // Determine display status (already-produced, then hold, then OOS, then production status)
  const displayStatus: DisplayStatus | null = useMemo(() => {
    // Outranks the hold countdown for the same reason the backend ranks it
    // above HELD: the garments physically exist, so there is nothing to wait for.
    if (isAlreadyInProduction) {
      return 'IN_PRODUCTION';
    }
    // BLU-718: a held order is NOT "Ready". Without this the row rendered a
    // green Ready badge (production_status is still NEW) right next to its own
    // amber hold countdown — and createBatch then rejected it.
    if (holdText) {
      return 'ON_HOLD';
    }
    // The backend's stock verdict, the same one its Ready tab filters on: an
    // order with an open out-of-stock issue is OOS here even with stock back.
    // BLU-336: priority-resolved orders are never blocked.
    if (isStockBlocked(order)) {
      return 'OOS';
    }
    if (order.prod_order?.production_status) {
      return order.prod_order.production_status as DisplayStatus;
    }
    return null; // Will show "Ready" badge
  }, [holdText, isAlreadyInProduction, order]);

  // BLU-1506: a client's own order the floor's owner is not ready to batch.
  // It reads as an ordinary order, greyed, with no reason given (no waiting,
  // supplier or PO words reach a client), and it outranks every other state,
  // as the server's queue lane does.
  const isNotBatchable = order.notBatchable === true;
  // Its words go grey (muted text, never opacity: the "Not ready yet" pill must
  // keep its full contrast, and so must every pill on the row).
  const rowText = isNotBatchable ? 'text-muted-foreground' : 'text-foreground';

  // Why this row cannot be selected for a batch, or null when it can be.
  // One string drives the disabled checkbox and its tooltip.
  const notSelectableReason: string | null = isNotBatchable
    ? t('orders.notBatchable.notSelectable')
    : isAlreadyInProduction
    ? (order.in_production_batch_number
        ? t('orders.inBatch.notSelectableNumbered', { number: order.in_production_batch_number })
        : t('orders.inBatch.notSelectable'))
    : holdText
      ? t('orders.hold.notSelectable', { time: holdText })
      : null;

  const shopifyUrl = getShopifyAdminOrderUrl(shopifyDomain, order.shopify_order_id);

  // Client chat link: opens this order's thread in the Client messages inbox.
  // TEST orders never resolve a thread (the resolver skips test fulfilment
  // orders), and the inbox rejects order=0 (the DAO maps a missing Shopify id
  // to 0), so neither gets a link.
  const showClientChat =
    canOpenClientChat && isCrossOrg && !order.prod_order?.is_test && order.shopify_order_id > 0;
  const clientChatHref = `/production/client-messages?order=${order.shopify_order_id}&client=${order.organization_id}`;

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // BLU-718: held orders cannot be batched — createBatch rejects the WHOLE
    // batch if one slips in. Guard here, not on the <Checkbox>: the click
    // handler lives on the wrapping div, and the Checkbox is aria-hidden with
    // no onCheckedChange, so `disabled` on it alone would not block selection.
    if (notSelectableReason) return;
    onToggleSelect?.(order.id, e);
  };

  const isWide = layout.density === 'wide';
  const isTest = !!order.prod_order?.is_test;
  // Phase 104 (D-14 / D-16): a manual order typed in on the queue. Its
  // shopify_order_id is a negative synthetic id, so the Shopify link and the
  // client chat above already resolve to nothing; nothing to change there.
  const isManual = order.is_manual === true;
  const manual = isManual ? (order.manual ?? null) : null;
  const manualCustomer = manual
    ? [manual.customer_company, manual.customer_name ?? order.customer_name]
        .filter((part): part is string => !!part && part.trim().length > 0)
        .join(' · ')
    : '';
  const manualLineNotes = manual?.line_notes.filter((n) => n.note.trim().length > 0) ?? [];
  // Phase 104 (D-21): editable until it is batched (the edit page re-checks
  // and opens read-only if a batch got there first).
  const manualEditPath =
    canEditManualOrders &&
    manual?.manual_order_id &&
    !order.prod_order?.batch_id &&
    !order.in_production_batch_id &&
    order.eligibility_status !== 'IN_PRODUCTION' &&
    order.eligibility_status !== 'SHIPPED'
      ? `/production/orders/manual/${manual.manual_order_id}`
      : null;
  const testPill = <TestOrderBadge size="sm" className={TEST_PILL} />;

  // The row's exception pills, built ONCE in the approved order (blockers
  // first) and fed to both the wide Flags cell and the flag button, so order
  // and style cannot drift. Each shows only when its condition holds AND the
  // caller's Display setting is on; TEST ignores the settings (hiding it risks
  // treating a fake order as real).
  const flagPills: FlagPill[] = [];
  if (isTest) {
    flagPills.push({ key: 'test', label: 'Test', isTest: true, node: testPill });
  }
  if (holdText && badgeVisibility.holdCountdown) {
    const label = t('orders.hold.pending', { time: holdText });
    flagPills.push({
      key: 'hold',
      label,
      node: (
        <Badge variant="secondary" size="sm" className={cn(FLAG_PILL, 'text-amber-700 bg-amber-50 border-amber-200')}>
          <Clock className="h-3 w-3" aria-hidden="true" />
          {label}
        </Badge>
      ),
    });
  }
  // BLU-712: the reason the order is absent from Ready to Batch. Amber like
  // the hold: waiting on something external, not an error.
  if (order.portless_blocked && badgeVisibility.portlessLabelNotReady) {
    const label = t('orders.portlessBlocked.label');
    flagPills.push({
      key: 'portlessBlocked',
      label,
      node: (
        <Badge
          variant="secondary"
          size="sm"
          className={cn(FLAG_PILL, 'text-amber-700 bg-amber-50 border-amber-200')}
          title={t('orders.portlessBlocked.tooltip')}
        >
          <PackageX className="h-3 w-3" aria-hidden="true" />
          {label}
        </Badge>
      ),
    });
  }
  // BLU-336: re-queued with priority after an OOS fix.
  if (order.is_priority && !holdText && !isAlreadyInProduction && badgeVisibility.resolved) {
    const label = t('orders.resolved');
    flagPills.push({
      key: 'resolved',
      label,
      node: (
        <Badge
          variant="outline"
          size="sm"
          className={cn(FLAG_PILL, 'border-lime-400 bg-lime-50 text-lime-700 dark:border-lime-700 dark:bg-lime-950/30 dark:text-lime-400')}
        >
          <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
          {label}
        </Badge>
      ),
    });
  }
  // Some line's SKU is archived in this order's own org.
  if (hasArchivedSku && badgeVisibility.archivedSku) {
    flagPills.push({
      key: 'archivedSku',
      label: t('orders.archivedSku.label'),
      node: <ArchivedSkuPill className={ROW_PILL} />,
    });
  }
  // Hybrid: Portless adds its own half, so the item count is not the whole
  // parcel. Glyph + label, no title-only meaning (PRODUCTION_DESIGN.md §10).
  if (hybrid && badgeVisibility.hybrid) {
    const label = t(hybrid.labelKey);
    flagPills.push({
      key: 'hybrid',
      label,
      node: (
        <Badge variant={hybrid.variant} size="sm" className={FLAG_PILL}>
          <hybrid.icon className="h-3 w-3" aria-hidden="true" />
          {label}
        </Badge>
      ),
    });
  }
  // The positive counterpart: only when NOT blocked and a label exists, so a
  // floor with nothing to do with Portless gets no badge at all.
  if (!order.portless_blocked && order.portless_label_ready_at && badgeVisibility.portlessLabelReady) {
    const label = t(labelReadyBadge.labelKey);
    flagPills.push({
      key: 'labelReady',
      label,
      node: (
        <Badge
          variant={labelReadyBadge.variant}
          size="sm"
          className={FLAG_PILL}
          title={t(labelReadyBadge.descriptionKey)}
        >
          <labelReadyBadge.icon className="h-3 w-3" aria-hidden="true" />
          {label}
        </Badge>
      ),
    });
  }
  // Compact / narrow: TEST stays on the row (start of Customer), so the flag
  // button counts and lists only the others.
  const buttonPills = flagPills.filter((p) => !p.isTest);

  const orderLink = shopifyUrl ? (
    <a
      href={shopifyUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={`#${orderNumber}`}
      className={cn(
        'inline-block max-w-full truncate align-middle font-semibold tabular-nums hover:underline',
        isNotBatchable ? 'text-muted-foreground' : 'text-primary'
      )}
      onClick={(e) => e.stopPropagation()}
    >
      #{orderNumber}
    </a>
  ) : (
    <span title={`#${orderNumber}`} className={cn('truncate font-semibold tabular-nums', rowText)}>
      #{orderNumber}
    </span>
  );

  const renderCell = (key: QueueColumnKey): ReactNode => {
    switch (key) {
      case 'select':
        if (!showCheckbox) return null;
        // Disabled while the order is held (BLU-718) or stranded in a batch.
        return notSelectableReason ? (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* cursor-not-allowed sits on the wrapper because it also
                  covers the p-1 padding. The Checkbox primitive already
                  ships disabled:opacity-50, so `disabled` alone is the
                  repo's dimmed treatment — no extra opacity class. */}
              <div className="p-1 -m-1 cursor-not-allowed" onClick={handleCheckboxClick}>
                <Checkbox
                  checked={isSelected}
                  disabled
                  tabIndex={-1}
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {notSelectableReason}
            </TooltipContent>
          </Tooltip>
        ) : (
          <div className="p-1 -m-1" onClick={handleCheckboxClick}>
            <Checkbox
              checked={isSelected}
              tabIndex={-1}
              className="h-5 w-5 transition-transform duration-150 active:scale-95"
              aria-hidden="true"
            />
          </div>
        );
      case 'expand':
        return (
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform duration-200',
              isExpanded && 'rotate-90'
            )}
          />
        );
      case 'order':
        // Manual: the due pill sits under the order number, so how soon it is
        // due is read with the order, at every density.
        return isManual && order.due_date ? (
          <div className="flex min-w-0 flex-col items-start gap-0.5">
            {orderLink}
            <DuePill dueDate={order.due_date} />
          </div>
        ) : (
          orderLink
        );
      case 'client':
        return isCrossOrg ? (
          <ClientOrgChip label={orgIdToName?.[order.organization_id]} className={ROW_PILL} />
        ) : null;
      case 'customer':
        return (
          <>
            {/* Compact / narrow: TEST stays visible on the row. */}
            {!isWide && isTest ? testPill : null}
            {isManual ? (
              <Badge variant="default" size="sm" className={cn(FLAG_PILL, 'text-primary-strong')}>
                {t('orders.manual.pill')}
              </Badge>
            ) : null}
            {manual && manualCustomer ? (
              <div className="flex min-w-0 flex-col">
                <span title={manualCustomer} className={cn('truncate text-sm', rowText)}>
                  {manualCustomer}
                </span>
                {manual.po_number ? (
                  <span className="truncate text-xs text-muted-foreground">
                    {t('orders.manual.po', { po: manual.po_number })}
                  </span>
                ) : null}
              </div>
            ) : order.customer_name ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={cn('min-w-0 truncate text-sm', rowText)}>
                    {order.customer_name}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{order.customer_name}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <span className="truncate text-sm text-muted-foreground">{t('orders.noName')}</span>
            )}
          </>
        );
      case 'type':
        return (
          <>
            {hasPod && (
              <Badge variant="info" size="sm" className={ROW_PILL}>
                POD
              </Badge>
            )}
            {hasPap && (
              <Badge variant="success" size="sm" className={ROW_PILL}>
                P&P
              </Badge>
            )}
            {!hasPod && !hasPap && (
              <span className="text-xs text-muted-foreground">-</span>
            )}
          </>
        );
      case 'items':
        return (
          <span className="whitespace-nowrap text-sm text-muted-foreground tabular-nums">
            {t('itemCount', { count: totalQuantity })}
          </span>
        );
      case 'status':
        // BLU-1506: never "Ready" on a row that cannot be selected (the BLU-718
        // rule). A neutral pill, in words, so the state is not only the tooltip.
        if (isNotBatchable) {
          const label = t('orders.notBatchable.status');
          return (
            <Badge
              variant="pending"
              className="min-w-0 max-w-full gap-1.5"
              title={label}
              data-testid="not-batchable-status"
            >
              <CircleDashed className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{label}</span>
            </Badge>
          );
        }
        // Blocked by stock: the badge opens the SKU count and a link to the
        // order's OOS issues. Never hideable from Display settings (those
        // cover flag pills only). No count yet (a backend that does not send
        // one) falls back to the plain badge.
        if (displayStatus === 'OOS' && (order.oos_sku_count ?? 0) > 0) {
          return (
            <OosStatusPopover
              orderName={order.order_number}
              skuCount={order.oos_sku_count ?? 0}
              canOpenIssues={canOpenIssues}
            />
          );
        }
        // "In batch #N" here is only the rare stranded case; the queue lists
        // NEW, unbatched orders.
        return (
          <ProductionStatusBadge
            status={displayStatus}
            batchNumber={order.in_production_batch_number ?? null}
            truncate
          />
        );
      case 'flags':
        // Every exception pill lives here, or behind the flag button at compact widths.
        return flagPills.map((p) => <Fragment key={p.key}>{p.node}</Fragment>);
      case 'age':
        return (
          <span className="whitespace-nowrap text-sm text-muted-foreground tabular-nums">
            {formatAge(order.order_date)}
          </span>
        );
      case 'flagsButton':
        return buttonPills.length > 0 ? (
          <OrderFlagsButton
            orderLabel={orderNumber}
            count={buttonPills.length}
            description={buttonPills.map((p) => p.label).join(', ')}
          >
            {buttonPills.map((p) => <Fragment key={p.key}>{p.node}</Fragment>)}
          </OrderFlagsButton>
        ) : null;
      case 'chat':
        return showClientChat ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button asChild variant="ghost" size="icon" className="relative h-7 w-7 shrink-0">
                <Link
                  to={clientChatHref}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={
                    clientChatUnread > 0
                      ? t('orders.clientChat.openUnread', { number: orderNumber, count: clientChatUnread })
                      : t('orders.clientChat.open', { number: orderNumber })
                  }
                >
                  <MessagesSquare className="h-4 w-4" aria-hidden="true" />
                  {clientChatUnread > 0 && (
                    <span
                      aria-hidden="true"
                      data-testid="client-chat-unread-dot"
                      className="absolute right-1 top-1 h-2 w-2 rounded-full bg-primary ring-2 ring-background"
                    />
                  )}
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {clientChatUnread > 0
                ? t('orders.clientChat.tooltipUnread', { count: clientChatUnread })
                : t('orders.clientChat.tooltip')}
            </TooltipContent>
          </Tooltip>
        ) : null;
      case 'actions':
        // Put Order On Hold / Skip Waiting Period. stopPropagation so it doesn't toggle the row.
        // BLU-1506: neither applies to a row that cannot be batched yet.
        if (isNotBatchable || (!onPutOnHold && !onSkipHold)) return null;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 opacity-60 hover:opacity-100"
                aria-label={t('orders.actions.menuLabel')}
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              {onPutOnHold && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onPutOnHold(order);
                  }}
                >
                  <Pause className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t('orders.actions.putOnHold')}
                </DropdownMenuItem>
              )}
              {isHeld && onSkipHold && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onSkipHold(order);
                  }}
                >
                  <FastForward className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t('orders.actions.skipHold')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
    }
  };

  return (
    <Collapsible.Root open={isExpanded} onOpenChange={() => onToggleExpand(order.id)}>
      {/* Row header - clicking row expands/collapses */}
      <Collapsible.Trigger asChild>
        <div
          role="button"
          tabIndex={0}
          data-testid="queue-row"
          data-not-batchable={isNotBatchable || undefined}
          className={cn(
            'grid items-center py-3 cursor-pointer transition-colors duration-150',
            // Padding + the 4px ribbon, shared with the column header and the
            // tier maths (queue-columns.ts). The ribbon is on every row so it
            // never shifts content.
            QUEUE_ROW_CHROME_CLASS,
            'hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            // BLU-389 review: provenance must stay visible when a cross-org row
            // is selected, so selection on those rows is bg-primary/5 only.
            isCrossOrg
              ? 'border-l-threadheads-purple/30'
              : isSelected
                ? 'border-l-primary'
                : 'border-l-transparent',
            isSelected && 'bg-primary/5',
            isExpanded && 'border-b-0'
          )}
          style={{ gridTemplateColumns: layout.gridTemplateColumns, columnGap: layout.columnGap }}
        >
          {layout.columns.map((key) => (
            <div
              key={key}
              data-col={key}
              className={cn(
                'flex min-w-0 items-center',
                key === 'customer' && 'gap-1.5',
                key === 'type' && 'gap-1',
                key === 'flags' && 'flex-wrap gap-1',
                // BLU-1506: a row that cannot be batched yet reads grey through
                // muted text, never opacity (opacity-60 took the "Not ready
                // yet" pill to about 2.8:1). The status cell keeps its pill's
                // own colours; the checkbox's disabled state already dims it.
                isNotBatchable && key !== 'select' && key !== 'status' && 'text-muted-foreground'
              )}
            >
              {renderCell(key)}
            </div>
          ))}
        </div>
      </Collapsible.Trigger>

      {/* Expanded content: starts under the Order column. */}
      <Collapsible.Content>
        <div
          className={cn(QUEUE_ROW_PADDING_CLASS, 'pb-4 pt-2 bg-muted/20')}
          style={{ marginLeft: orderColumnOffsetPx(layout.density) - QUEUE_ROW_PADDING_PX }}
        >
          {/* Line Items Table */}
          <OrderLineItemsTable lineItems={order.line_items} />
          {/* Phase 104 (D-16): a manual order's notes for the floor, one per line. */}
          {manualLineNotes.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm" aria-label={t('orders.manual.lineNotesLabel')}>
              {manualLineNotes.map((n) => {
                const garment = [n.garment_title, n.colour_label].filter(Boolean).join(' · ');
                return (
                  <li key={n.line_id} className="flex min-w-0 flex-wrap gap-x-2">
                    {garment && <span className="font-medium text-foreground">{garment}</span>}
                    <span className="min-w-0 text-muted-foreground [overflow-wrap:anywhere]">
                      {t('orders.manual.lineNote', { note: n.note })}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {manualEditPath && (
            <Button asChild variant="outline" className="mt-3 h-8 gap-1.5 px-3 text-xs">
              <Link to={manualEditPath} aria-label={t('orders.manual.editOrderLabel', { order: orderNumber })}>
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                {t('orders.manual.editOrder')}
              </Link>
            </Button>
          )}
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
});
